import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import fetchWrapper from '../utils/httpClient';
import {
  API_GET_RESOURCE_BY_CONTENT_TYPE,
  API_GET_RESOURCE_PACKAGES,
  API_ADD_RESOURCE,
  API_UPDATE_RESOURCE,
} from '../utils/apis';

// Asynchronous thunk for fetching resources by content type
export const fetchResources = createAsyncThunk(
  'resources/fetchResources',
  async () => {
    try {
      const response = await fetchWrapper.get(API_GET_RESOURCE_BY_CONTENT_TYPE);
      return response.resdata;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

// Async Thunk: edit
export const fetchResourcePackageById = createAsyncThunk(
  'resources/fetchResourcePackageById',
  async ({ guid, contentType = 'page', language = 'EN' }) => {
    try {
      const url = `${API_GET_RESOURCE_PACKAGES}/${guid}?contentType=${contentType}&language=${language}`;
      const response = await fetchWrapper.get(url);

      let resdata = response.resdata;
      if (typeof resdata === 'string') {
        resdata = JSON.parse(resdata);
      }

      return resdata;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

// Async Thunk: delete
export const deleteResourcePackage = createAsyncThunk(
  'resources/deleteResourcePackage',
  async ({ guid, language = 'EN' }) => {
    try {
      const url = `${API_GET_RESOURCE_PACKAGES}/${guid}?contentType=page&language=${language}`;
      const response = await fetchWrapper.delete(url);
      return { guid, language, ...response };
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

// Async Thunk: create
export const createResource = createAsyncThunk(
  'resources/createResource',
  async (resourceData) => {
    try {
      const response = await fetchWrapper.post(API_ADD_RESOURCE, resourceData);
      return response.resdata;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

// Async Thunk: update 
export const updateResource = createAsyncThunk(
  'resources/updateResource',
  async ({
    guid,
    menu = 'packages',
    language = 'EN',
    content
  }) => {
    try {
      const payload = {
        contentType: 'page',
        menu,
        language,
        guid,
        content: typeof content === 'string' ? content : JSON.stringify(content)
      };

      const response = await fetchWrapper.put(API_UPDATE_RESOURCE, payload);
      return response.resdata;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

// Slice
const resourceSlice = createSlice({
  name: 'resources',
  initialState: {
    data: null,
    selectedPackage: null,
    loading: false,
    packageLoading: false,
    deleteLoading: false,
    createLoading: false,
    updateLoading: false,
    error: null,
    packageError: null,
    deleteError: null,
    createError: null,
    updateError: null,
    status: 'idle',
    packageStatus: 'idle',
    deleteStatus: 'idle',
    createStatus: 'idle',
    updateStatus: 'idle',
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.packageError = null;
      state.deleteError = null;
      state.createError = null;
      state.updateError = null;
    },
    clearSelectedPackage: (state) => {
      state.selectedPackage = null;
      state.packageError = null;
      state.packageStatus = 'idle';
    },
    clearDeleteStatus: (state) => {
      state.deleteError = null;
      state.deleteStatus = 'idle';
    },
    clearCreateStatus: (state) => {
      state.createError = null;
      state.createStatus = 'idle';
    },
    clearUpdateStatus: (state) => {
      state.updateError = null;
      state.updateStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle different states of fetchResources
      .addCase(fetchResources.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchResources.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchResources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = 'failed';
      })

      //Fetch Resource Package
      .addCase(fetchResourcePackageById.pending, (state) => {
        state.packageLoading = true;
        state.packageError = null;
        state.packageStatus = 'loading';
      })
      .addCase(fetchResourcePackageById.fulfilled, (state, action) => {
        state.packageLoading = false;
        state.selectedPackage = action.payload;
        state.packageStatus = 'succeeded';
      })
      .addCase(fetchResourcePackageById.rejected, (state, action) => {
        state.packageLoading = false;
        state.packageError = action.error.message;
        state.packageStatus = 'failed';
      })

      //Delete 
      .addCase(deleteResourcePackage.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.deleteStatus = 'loading';
      })
      .addCase(deleteResourcePackage.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteStatus = 'succeeded';
      })
      .addCase(deleteResourcePackage.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.error.message;
        state.deleteStatus = 'failed';
      })

      .addCase(createResource.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.createError = null;
        state.status = 'loading';
      })
      .addCase(createResource.fulfilled, (state) => {
        state.loading = false;
        state.status = 'succeeded';
      })
      .addCase(createResource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.createError = action.error.message;
        state.status = 'failed';
      })

      // Update
      .addCase(updateResource.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateStatus = 'loading';
      })
      .addCase(updateResource.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateStatus = 'succeeded';
      })
      .addCase(updateResource.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.error.message;
        state.updateStatus = 'failed';
      });
  },
});

// Action creators
export const {
  clearError,
  clearSelectedPackage,
  clearDeleteStatus,
  clearCreateStatus,
  clearUpdateStatus,
} = resourceSlice.actions;

export const selectAllResources = (state) => state.resources.data;
export const selectResourcesLoading = (state) => state.resources.loading;
export const selectResourcesError = (state) => state.resources.error;
export const selectResourcesStatus = (state) => state.resources.status;

export const selectSelectedPackage = (state) => state.resources.selectedPackage;
export const selectPackageLoading = (state) => state.resources.packageLoading;
export const selectPackageError = (state) => state.resources.packageError;
export const selectPackageStatus = (state) => state.resources.packageStatus;

export const selectDeleteLoading = (state) => state.resources.deleteLoading;
export const selectDeleteError = (state) => state.resources.deleteError;
export const selectDeleteStatus = (state) => state.resources.deleteStatus;

export const selectCreateLoading = (state) => state.resources.loading;
export const selectCreateError = (state) => state.resources.error;
export const selectCreateStatus = (state) => state.resources.status;

export const selectUpdateLoading = (state) => state.resources.updateLoading;
export const selectUpdateError = (state) => state.resources.updateError;
export const selectUpdateStatus = (state) => state.resources.updateStatus;

export default resourceSlice.reducer;